import { useState, useRef, useCallback, useEffect } from "react";
import {
  Video, VideoOff, Mic, MicOff, Monitor, Camera, Play, Square,
  RotateCcw, Radio, Settings, Upload, Volume2, VolumeX, Maximize2,
  ChevronDown, X, Tv, Film, MonitorPlay, Plus, Layers, Trash2,
  Eye, EyeOff, Clock
} from "lucide-react";
import { useScoreBirdData } from "./ScoreBirdGameCast";

const API = process.env.REACT_APP_BACKEND_URL || "";

const SOURCE_TYPES = { CAMERA: "camera", SCREEN: "screen", VIDEO: "video" };

/* ═══════════════════════════════════════════════════════
   SCORE BUG OVERLAY — rendered on the program canvas
   ═══════════════════════════════════════════════════════ */
const ScoreBugOverlay = ({ game, visible }) => {
  if (!visible || !game) return null;
  const awayName = game.away_team_name || game.away_team?.name || "AWAY";
  const homeName = game.home_team_name || game.home_team?.name || "HOME";
  const awayScore = game.away_score ?? "0";
  const homeScore = game.home_score ?? "0";
  const clock = game.clock || game.game_clock || "";
  const period = game.quarter || game.period || "";
  const periodLabel = game.sport === "basketball"
    ? (parseInt(period) > 4 ? `OT${parseInt(period) - 4}` : `Q${period}`)
    : (parseInt(period) > 4 ? `OT${parseInt(period) - 4}` : `Q${period}`);
  const awayAbbr = game.away_team?.abbreviation || awayName.substring(0, 3).toUpperCase();
  const homeAbbr = game.home_team?.abbreviation || homeName.substring(0, 3).toUpperCase();

  return (
    <div className="absolute bottom-6 left-6 z-20 pointer-events-none select-none" data-testid="score-bug-overlay">
      <div className="flex shadow-2xl text-white" style={{ fontFamily: "'Arial Black', 'Helvetica Neue', sans-serif" }}>
        {/* Away */}
        <div className="flex items-center bg-zinc-900/95 border border-zinc-700">
          <div className="px-3 py-1.5 text-[13px] font-black tracking-wide min-w-[52px] text-center">{awayAbbr}</div>
          <div className="px-3 py-1.5 text-[18px] font-black bg-black/60 min-w-[36px] text-center tabular-nums">{awayScore}</div>
        </div>
        {/* Home */}
        <div className="flex items-center bg-zinc-900/95 border-t border-b border-r border-zinc-700">
          <div className="px-3 py-1.5 text-[13px] font-black tracking-wide min-w-[52px] text-center">{homeAbbr}</div>
          <div className="px-3 py-1.5 text-[18px] font-black bg-black/60 min-w-[36px] text-center tabular-nums">{homeScore}</div>
        </div>
        {/* Clock / Period */}
        <div className="flex flex-col items-center justify-center bg-zinc-800/95 border-t border-b border-r border-zinc-700 px-2.5 min-w-[60px]">
          <div className="text-[10px] font-bold text-orange-400">{periodLabel}</div>
          <div className="text-[12px] font-black tabular-nums">{clock}</div>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   AUDIO METER — vertical dB-style
   ═══════════════════════════════════════════════════════ */
const AudioMeter = ({ stream, label }) => {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  useEffect(() => {
    if (!stream) return;
    const actx = new (window.AudioContext || window.webkitAudioContext)();
    const analyser = actx.createAnalyser();
    analyser.fftSize = 256;
    const src = actx.createMediaStreamSource(stream);
    src.connect(analyser);
    const data = new Uint8Array(analyser.frequencyBinCount);
    const draw = () => {
      analyser.getByteFrequencyData(data);
      const c = canvasRef.current;
      if (!c) return;
      const ctx = c.getContext("2d");
      const avg = data.reduce((a, b) => a + b, 0) / data.length;
      const level = avg / 255;
      ctx.clearRect(0, 0, c.width, c.height);
      const barH = c.height * level;
      const grad = ctx.createLinearGradient(0, c.height, 0, 0);
      grad.addColorStop(0, "#22c55e");
      grad.addColorStop(0.6, "#eab308");
      grad.addColorStop(0.85, "#ef4444");
      ctx.fillStyle = grad;
      ctx.fillRect(0, c.height - barH, c.width, barH);
      // dB lines
      ctx.strokeStyle = "rgba(255,255,255,0.1)";
      for (let i = 0; i < 10; i++) {
        const y = (c.height / 10) * i;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(c.width, y);
        ctx.stroke();
      }
      animRef.current = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animRef.current); actx.close(); };
  }, [stream]);
  return (
    <div className="flex flex-col items-center gap-1">
      <canvas ref={canvasRef} width={16} height={120} className="rounded-sm bg-zinc-950 border border-zinc-700" />
      {label && <span className="text-[9px] text-zinc-500 font-medium">{label}</span>}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   CAMERA SLOT — 2x2 grid item
   ═══════════════════════════════════════════════════════ */
const CameraSlot = ({ source, index, isProgram, onSelect, onAdd }) => {
  const videoRef = useRef(null);
  useEffect(() => {
    if (videoRef.current && source?.stream) {
      videoRef.current.srcObject = source.stream;
    }
  }, [source?.stream]);

  if (!source) {
    return (
      <div
        onClick={onAdd}
        className="relative bg-zinc-950 border border-zinc-800 flex flex-col items-center justify-center cursor-pointer hover:border-zinc-600 transition-colors group"
        data-testid={`camera-slot-${index}`}
      >
        <Plus className="w-8 h-8 text-zinc-700 group-hover:text-zinc-500 transition-colors" />
        <span className="text-[11px] text-zinc-600 mt-1.5 font-medium">Camera {index + 1}</span>
      </div>
    );
  }

  return (
    <div
      onClick={() => onSelect(source.id)}
      className={`relative bg-black border-2 cursor-pointer transition-all overflow-hidden ${
        isProgram ? "border-red-500" : "border-zinc-800 hover:border-zinc-600"
      }`}
      data-testid={`camera-slot-${index}`}
    >
      {source.stream ? (
        <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-contain" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <Film className="w-6 h-6 text-zinc-600" />
        </div>
      )}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-2 py-1">
        <span className="text-[10px] text-white font-medium truncate block">{source.label}</span>
      </div>
      {isProgram && (
        <div className="absolute top-1 right-1 bg-red-600 text-[8px] text-white font-bold px-1.5 py-0.5 rounded uppercase">PGM</div>
      )}
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════
   MAIN PRODUCTION STUDIO — Production Truck-inspired layout
   ════════════════════════════════════════════════════════════════ */
export default function ProductionStudio() {
  /* ── Sources ── */
  const [sources, setSources] = useState([]);
  const [programSourceId, setProgramSourceId] = useState(null);
  const nextSourceId = useRef(1);

  /* ── Canvas compositor ── */
  const canvasRef = useRef(null);
  const programStreamRef = useRef(null);
  const animFrameRef = useRef(null);

  /* ── Streaming ── */
  const [isLive, setIsLive] = useState(false);
  const [rtmpUrl, setRtmpUrl] = useState("");
  const [streamKey, setStreamKey] = useState("");
  const [streamStatus, setStreamStatus] = useState("");
  const wsRef = useRef(null);
  const recorderRef = useRef(null);

  /* ── Replay ── */
  const replayBuffers = useRef({});
  const [replayDuration, setReplayDuration] = useState(30);
  const [customReplayTime, setCustomReplayTime] = useState("");
  const [isPlayingReplay, setIsPlayingReplay] = useState(false);
  const replayVideoRef = useRef(null);

  /* ── Audio ── */
  const [masterMuted, setMasterMuted] = useState(false);
  const audioCtxRef = useRef(null);
  const gainNodeRef = useRef(null);
  const destinationRef = useRef(null);
  const audioSourceNodes = useRef({});

  /* ── Scoreboard overlay ── */
  const [scorebirdLink, setScorebirdLink] = useState("");
  const [scorebirdActive, setScorebirdActive] = useState(false);
  const [showOverlayPanel, setShowOverlayPanel] = useState(false);
  const { data: scorebirdData } = useScoreBirdData(scorebirdActive ? scorebirdLink : null);
  const [showScoreBug, setShowScoreBug] = useState(false);

  /* ── Settings ── */
  const [resolution, setResolution] = useState("1920x1080");
  const [activeBottomTab, setActiveBottomTab] = useState("clips"); // clips | replay | overlay
  const [elapsedTime, setElapsedTime] = useState(0);
  const timerRef = useRef(null);

  /* ── Timer when live ── */
  useEffect(() => {
    if (isLive) {
      setElapsedTime(0);
      timerRef.current = setInterval(() => setElapsedTime(t => t + 1), 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [isLive]);

  const formatTime = (s) => {
    const h = Math.floor(s / 3600).toString().padStart(2, "0");
    const m = Math.floor((s % 3600) / 60).toString().padStart(2, "0");
    const sec = (s % 60).toString().padStart(2, "0");
    return `${h}:${m}:${sec}`;
  };

  /* ── Canvas compositor loop ── */
  const compositorLoop = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const [w, h] = resolution.split("x").map(Number);
    canvas.width = w;
    canvas.height = h;
    const drawFrame = () => {
      ctx.fillStyle = "#000";
      ctx.fillRect(0, 0, w, h);
      const pgm = sources.find(s => s.id === programSourceId);
      if (pgm) {
        let videoEl = document.querySelector(`[data-testid="camera-slot-${sources.indexOf(pgm)}"] video`);
        if (!videoEl) {
          // Try finding by iterating slots
          for (let i = 0; i < 4; i++) {
            const slot = document.querySelector(`[data-testid="camera-slot-${i}"] video`);
            if (slot) { videoEl = slot; break; }
          }
        }
        if (pgm.videoElement) videoEl = pgm.videoElement;
        if (videoEl && videoEl.readyState >= 2) {
          const vw = videoEl.videoWidth || w;
          const vh = videoEl.videoHeight || h;
          const scale = Math.min(w / vw, h / vh);
          const dw = vw * scale;
          const dh = vh * scale;
          ctx.drawImage(videoEl, (w - dw) / 2, (h - dh) / 2, dw, dh);
        }
      }
      animFrameRef.current = requestAnimationFrame(drawFrame);
    };
    drawFrame();
    programStreamRef.current = canvas.captureStream(30);
  }, [sources, programSourceId, resolution]);

  useEffect(() => {
    compositorLoop();
    return () => cancelAnimationFrame(animFrameRef.current);
  }, [compositorLoop]);

  /* ── Audio setup ── */
  useEffect(() => {
    if (!audioCtxRef.current) {
      const actx = new (window.AudioContext || window.webkitAudioContext)();
      const gain = actx.createGain();
      const dest = actx.createMediaStreamDestination();
      gain.connect(dest);
      audioCtxRef.current = actx;
      gainNodeRef.current = gain;
      destinationRef.current = dest;
    }
  }, []);

  useEffect(() => {
    if (gainNodeRef.current) gainNodeRef.current.gain.value = masterMuted ? 0 : 1;
  }, [masterMuted]);

  useEffect(() => {
    const actx = audioCtxRef.current;
    if (!actx || !programSourceId) return;
    Object.values(audioSourceNodes.current).forEach(n => { try { n.disconnect(); } catch {} });
    audioSourceNodes.current = {};
    const pgm = sources.find(s => s.id === programSourceId);
    if (pgm?.stream?.getAudioTracks().length > 0) {
      const node = actx.createMediaStreamSource(pgm.stream);
      node.connect(gainNodeRef.current);
      audioSourceNodes.current[pgm.id] = node;
    }
  }, [programSourceId, sources]);

  /* ── Replay recording per source ── */
  useEffect(() => {
    sources.forEach(src => {
      if (src.stream && !replayBuffers.current[src.id]) {
        try {
          const mr = new MediaRecorder(src.stream, { mimeType: "video/webm;codecs=vp8,opus", videoBitsPerSecond: 4000000 });
          const chunks = [];
          mr.ondataavailable = (e) => {
            if (e.data.size > 0) {
              chunks.push({ data: e.data, ts: Date.now() });
              const cutoff = Date.now() - Math.max(replayDuration, 120) * 1000;
              while (chunks.length > 0 && chunks[0].ts < cutoff) chunks.shift();
            }
          };
          mr.start(1000);
          replayBuffers.current[src.id] = { recorder: mr, chunks };
        } catch {}
      }
    });
  }, [sources, replayDuration]);

  /* ── Add source helpers ── */
  const addCamera = async () => {
    try {
      const usedIds = sources.filter(s => s.type === SOURCE_TYPES.CAMERA).map(s => s.deviceId);
      const devices = await navigator.mediaDevices.enumerateDevices();
      const available = devices.filter(d => d.kind === "videoinput" && !usedIds.includes(d.deviceId));
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1920 }, height: { ideal: 1080 }, frameRate: { ideal: 30 }, ...(available[0] ? { deviceId: { exact: available[0].deviceId } } : {}) },
        audio: true,
      });
      const track = stream.getVideoTracks()[0];
      const id = nextSourceId.current++;
      setSources(prev => [...prev, { id, type: SOURCE_TYPES.CAMERA, label: track.label || `Camera ${id}`, stream, deviceId: track.getSettings().deviceId }]);
      if (!programSourceId) setProgramSourceId(id);
    } catch (err) { alert("Could not access camera: " + err.message); }
  };

  const addScreen = async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: { width: { ideal: 1920 }, height: { ideal: 1080 }, frameRate: { ideal: 30 } }, audio: true });
      const id = nextSourceId.current++;
      const label = stream.getVideoTracks()[0].label || `Screen ${id}`;
      setSources(prev => [...prev, { id, type: SOURCE_TYPES.SCREEN, label, stream }]);
      if (!programSourceId) setProgramSourceId(id);
      stream.getVideoTracks()[0].onended = () => removeSource(id);
    } catch {}
  };

  const addVideoFile = () => {
    const input = document.createElement("input");
    input.type = "file"; input.accept = "video/*";
    input.onchange = (e) => {
      const file = e.target.files[0]; if (!file) return;
      const url = URL.createObjectURL(file);
      const ve = document.createElement("video");
      ve.src = url; ve.loop = true; ve.muted = false; ve.playsInline = true;
      ve.onloadeddata = () => {
        ve.play();
        const stream = ve.captureStream ? ve.captureStream() : ve.mozCaptureStream();
        const id = nextSourceId.current++;
        setSources(prev => [...prev, { id, type: SOURCE_TYPES.VIDEO, label: file.name.substring(0, 20), stream, videoElement: ve }]);
        if (!programSourceId) setProgramSourceId(id);
      };
    };
    input.click();
  };

  const removeSource = (id) => {
    setSources(prev => {
      const src = prev.find(s => s.id === id);
      if (src?.stream) src.stream.getTracks().forEach(t => t.stop());
      if (src?.videoElement) { src.videoElement.pause(); URL.revokeObjectURL(src.videoElement.src); }
      if (replayBuffers.current[id]) { try { replayBuffers.current[id].recorder.stop(); } catch {} delete replayBuffers.current[id]; }
      return prev.filter(s => s.id !== id);
    });
    if (programSourceId === id) setSources(prev => { if (prev.length > 0) setProgramSourceId(prev[0]?.id); else setProgramSourceId(null); return prev; });
  };

  /* ── Transition ── */
  const switchToSource = (id) => setProgramSourceId(id);

  /* ── Replay ── */
  const captureReplay = (duration) => {
    const d = duration || replayDuration;
    const buf = replayBuffers.current[programSourceId];
    if (!buf || buf.chunks.length === 0) { alert("No replay data available yet."); return; }
    const cutoff = Date.now() - d * 1000;
    const relevantChunks = buf.chunks.filter(c => c.ts >= cutoff);
    if (relevantChunks.length === 0) { alert("Not enough replay data for this duration."); return; }
    const blob = new Blob(relevantChunks.map(c => c.data), { type: "video/webm" });
    const url = URL.createObjectURL(blob);
    setIsPlayingReplay(true);
    if (replayVideoRef.current) {
      replayVideoRef.current.src = url;
      replayVideoRef.current.playbackRate = 0.5;
      replayVideoRef.current.play();
      replayVideoRef.current.onended = () => { setIsPlayingReplay(false); URL.revokeObjectURL(url); };
    }
  };

  /* ── Streaming ── */
  const startStreaming = () => {
    if (!rtmpUrl) { alert("Enter RTMP URL in settings."); return; }
    const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const ws = new WebSocket(`${wsProtocol}//${window.location.host}/api/production/stream`);
    wsRef.current = ws;
    ws.onopen = () => {
      setStreamStatus("Connecting...");
      ws.send(JSON.stringify({ type: "start", rtmp_url: rtmpUrl, stream_key: streamKey }));
      const canvasStream = programStreamRef.current;
      const audioDest = destinationRef.current;
      const combined = new MediaStream();
      canvasStream?.getVideoTracks().forEach(t => combined.addTrack(t));
      audioDest?.stream.getAudioTracks().forEach(t => combined.addTrack(t));
      try {
        const rec = new MediaRecorder(combined, { mimeType: "video/webm;codecs=vp8,opus", videoBitsPerSecond: 4000000 });
        rec.ondataavailable = (e) => { if (e.data.size > 0 && ws.readyState === WebSocket.OPEN) ws.send(e.data); };
        rec.start(1000);
        recorderRef.current = rec;
        setIsLive(true);
        setStreamStatus("Live");
      } catch (err) { setStreamStatus("Error: " + err.message); }
    };
    ws.onmessage = (e) => { try { const msg = JSON.parse(e.data); if (msg.type === "error") setStreamStatus("Error: " + msg.message); else if (msg.type === "started") setStreamStatus("Live"); else if (msg.type === "stopped") setStreamStatus("Stopped"); } catch {} };
    ws.onerror = () => setStreamStatus("WebSocket error");
    ws.onclose = () => { if (isLive) setStreamStatus("Disconnected"); };
  };

  const stopStreaming = () => {
    if (recorderRef.current?.state !== "inactive") { recorderRef.current?.stop(); recorderRef.current = null; }
    if (wsRef.current) { try { wsRef.current.send(JSON.stringify({ type: "stop" })); } catch {} wsRef.current.close(); wsRef.current = null; }
    setIsLive(false); setStreamStatus("");
  };

  useEffect(() => { return () => { stopStreaming(); sources.forEach(s => s.stream?.getTracks().forEach(t => t.stop())); }; }, []);

  const pgm = sources.find(s => s.id === programSourceId);
  const [w, h] = resolution.split("x").map(Number);

  // Assign sources to 4 slots
  const slots = [null, null, null, null];
  sources.forEach((s, i) => { if (i < 4) slots[i] = s; });

  return (
    <div className="h-screen bg-[#1a1d21] text-white flex flex-col overflow-hidden" data-testid="production-studio">
      {/* ════ TOP BAR ════ */}
      <div className="h-10 bg-[#2a2d32] border-b border-black/50 flex items-center justify-between px-3 flex-shrink-0">
        <div className="flex items-center gap-4">
          <span className="text-[13px] font-bold text-orange-500 tracking-wide italic">Production Studio</span>
          <div className="h-5 w-px bg-zinc-700" />
          <select className="bg-[#1a1d21] text-zinc-300 text-xs rounded px-2 py-1 border border-zinc-700 focus:outline-none" data-testid="rtmp-url-select">
            <option>Generic (RTMP)</option>
          </select>
          <div className="h-5 w-px bg-zinc-700" />
          {/* RTMP Config inline */}
          <input
            type="text" value={rtmpUrl} onChange={(e) => setRtmpUrl(e.target.value)} placeholder="RTMP URL"
            className="bg-[#1a1d21] text-zinc-300 text-xs rounded px-2 py-1 border border-zinc-700 w-56 focus:outline-none focus:border-orange-500"
            disabled={isLive} data-testid="rtmp-url-input"
          />
          <input
            type="password" value={streamKey} onChange={(e) => setStreamKey(e.target.value)} placeholder="Stream Key"
            className="bg-[#1a1d21] text-zinc-300 text-xs rounded px-2 py-1 border border-zinc-700 w-36 focus:outline-none focus:border-orange-500"
            disabled={isLive} data-testid="stream-key-input"
          />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-green-400 text-[11px] font-semibold">{w}x{h} @ 30fps</span>
          <div className="h-5 w-px bg-zinc-700" />
          <button
            className={`px-3 py-1 text-xs font-bold rounded transition-colors ${!isLive ? "bg-zinc-700 text-zinc-400" : "bg-zinc-700 text-zinc-300"}`}
            onClick={() => {}} data-testid="test-btn"
          >TEST</button>
          <button
            onClick={isLive ? stopStreaming : startStreaming}
            disabled={!isLive && sources.length === 0}
            className={`px-3 py-1 text-xs font-bold rounded transition-colors ${
              isLive ? "bg-red-600 text-white animate-pulse" : "bg-zinc-600 text-zinc-300 hover:bg-zinc-500 disabled:opacity-40"
            }`}
            data-testid="go-live-btn"
          >{isLive ? "LIVE" : "LIVE"}</button>
          <div className="h-5 w-px bg-zinc-700" />
          <div className="flex items-center gap-1.5">
            <div className={`w-2 h-2 rounded-full ${isLive ? "bg-red-500 animate-pulse" : "bg-zinc-600"}`} />
            <span className="text-[12px] font-mono text-zinc-300 tabular-nums w-16" data-testid="elapsed-timer">{formatTime(elapsedTime)}</span>
          </div>
        </div>
      </div>

      {/* ════ MAIN CONTENT ════ */}
      <div className="flex flex-1 min-h-0">
        {/* ── LEFT: 2x2 Camera Grid ── */}
        <div className="w-[340px] flex-shrink-0 p-1.5 flex flex-col">
          <div className="grid grid-cols-2 grid-rows-2 gap-1 flex-1">
            {slots.map((src, i) => (
              <CameraSlot
                key={i} source={src} index={i}
                isProgram={src?.id === programSourceId}
                onSelect={switchToSource}
                onAdd={i === sources.length ? addCamera : undefined}
              />
            ))}
          </div>
        </div>

        {/* ── CENTER: Transition Column + Program Output ── */}
        <div className="flex flex-1 min-w-0">
          {/* Transition buttons */}
          <div className="w-20 flex-shrink-0 flex flex-col items-center py-3 gap-2 border-l border-r border-zinc-800/50">
            {["CUT", "DISSOLVE", "SWIPE", "FLASH"].map(t => (
              <button
                key={t}
                onClick={() => { /* transitions would go here */ }}
                className="w-[72px] py-1.5 text-[11px] font-bold rounded bg-[#3a5a8c] hover:bg-[#4a6a9c] text-white transition-colors tracking-wide"
                data-testid={`transition-${t.toLowerCase()}`}
              >{t}</button>
            ))}
            <div className="flex-1" />
            {/* Add source shortcuts */}
            <button onClick={addCamera} className="w-[72px] py-1.5 text-[10px] font-semibold rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 flex items-center justify-center gap-1" data-testid="add-camera-btn">
              <Camera className="w-3 h-3" /> Camera
            </button>
            <button onClick={addScreen} className="w-[72px] py-1.5 text-[10px] font-semibold rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 flex items-center justify-center gap-1" data-testid="add-screen-btn">
              <Monitor className="w-3 h-3" /> Screen
            </button>
            <button onClick={addVideoFile} className="w-[72px] py-1.5 text-[10px] font-semibold rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 flex items-center justify-center gap-1" data-testid="add-video-btn">
              <Film className="w-3 h-3" /> Video
            </button>
            <div className="flex-1" />
            <button onClick={() => setShowOverlayPanel(p => !p)} className="p-2 rounded hover:bg-zinc-800 text-zinc-500 hover:text-zinc-300" title="Settings">
              <Settings className="w-5 h-5" />
            </button>
          </div>

          {/* Program Output */}
          <div className="flex-1 flex flex-col min-w-0">
            <div className="flex-1 p-2 flex items-center justify-center relative">
              <div className="relative w-full h-full max-h-full">
                <div className="relative bg-black rounded overflow-hidden border border-zinc-800 w-full h-full">
                  <canvas ref={canvasRef} className="w-full h-full object-contain" style={{ aspectRatio: `${w}/${h}` }} data-testid="program-canvas" />
                  {/* Score Bug Overlay */}
                  <ScoreBugOverlay game={scorebirdData} visible={showScoreBug && scorebirdData} />
                  {/* Replay Overlay */}
                  {isPlayingReplay && (
                    <div className="absolute inset-0 z-30">
                      <video ref={replayVideoRef} className="w-full h-full object-contain bg-black" data-testid="replay-video" />
                      <div className="absolute top-3 left-3 bg-yellow-500 text-black text-xs font-bold px-2 py-1 rounded tracking-wide">INSTANT REPLAY</div>
                      <button onClick={() => { setIsPlayingReplay(false); replayVideoRef.current?.pause(); }} className="absolute top-3 right-3 bg-black/70 p-1 rounded hover:bg-red-600">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── RIGHT SIDEBAR: Audio + Ad Controls ── */}
        <div className="w-24 flex-shrink-0 border-l border-zinc-800/50 flex flex-col items-center py-3 gap-3">
          {/* Source remove buttons */}
          {sources.slice(0, 4).map((src, i) => (
            <button key={src.id} onClick={() => removeSource(src.id)} className="text-zinc-600 hover:text-red-400 transition-colors" title={`Remove ${src.label}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          ))}
          <div className="flex-1" />

          {/* Resolution selector */}
          <select value={resolution} onChange={e => setResolution(e.target.value)} disabled={isLive}
            className="bg-zinc-900 text-zinc-400 text-[9px] rounded px-1 py-0.5 border border-zinc-700 w-[70px]" data-testid="resolution-select">
            <option value="1920x1080">1080p</option>
            <option value="1280x720">720p</option>
            <option value="854x480">480p</option>
          </select>

          <div className="h-px w-12 bg-zinc-800 my-1" />

          {/* Audio meters */}
          <span className="text-[9px] text-zinc-500 font-bold uppercase tracking-wider">Audio</span>
          <div className="flex gap-2">
            <AudioMeter stream={pgm?.stream} label="L" />
            <AudioMeter stream={pgm?.stream} label="R" />
          </div>
          <button
            onClick={() => setMasterMuted(!masterMuted)}
            className={`px-2 py-1 rounded text-[10px] font-bold transition-colors ${masterMuted ? "bg-red-700 text-white" : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"}`}
            data-testid="master-mute-btn"
          >{masterMuted ? "UNMUTE" : "MUTE"}</button>
        </div>
      </div>

      {/* ════ BOTTOM TOOLBAR ════ */}
      <div className="h-9 bg-[#2a2d32] border-t border-b border-black/50 flex items-center px-3 gap-1 flex-shrink-0">
        {[
          { key: "clips", label: "Video Clips", icon: Film },
          { key: "replay", label: "Capture Replay", icon: RotateCcw },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setActiveBottomTab(t.key)}
            className={`flex items-center gap-1.5 px-3 py-1 rounded text-[11px] font-semibold transition-colors ${
              activeBottomTab === t.key ? "bg-[#3a5a8c] text-white" : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
            }`}
            data-testid={`tab-${t.key}`}
          >
            <t.icon className="w-3.5 h-3.5" />
            {t.label}
          </button>
        ))}
        <div className="flex-1" />
        <button
          onClick={() => { setShowScoreBug(true); setScorebirdActive(true); }}
          disabled={!scorebirdLink}
          className={`px-4 py-1 rounded text-[11px] font-bold transition-colors ${
            showScoreBug ? "bg-green-700 text-white" : "bg-[#3a5a8c] text-white hover:bg-[#4a6a9c] disabled:opacity-40 disabled:cursor-not-allowed"
          }`}
          data-testid="overlay-btn"
        >OVERLAY</button>
        <button
          onClick={() => { setShowScoreBug(false); }}
          className="px-4 py-1 rounded text-[11px] font-bold bg-zinc-700 text-zinc-300 hover:bg-zinc-600 transition-colors"
          data-testid="clear-overlay-btn"
        >CLEAR</button>
      </div>

      {/* ════ BOTTOM PANEL ════ */}
      <div className="h-48 bg-[#1e2125] border-t border-zinc-800/50 flex flex-shrink-0">
        {/* Left: Tabs Content */}
        <div className="flex-1 p-3 overflow-auto">
          {activeBottomTab === "clips" && (
            <div>
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider font-bold mb-2">Video Clips & Commercials</p>
              <button onClick={addVideoFile} className="flex items-center gap-2 px-3 py-2 bg-zinc-800 hover:bg-zinc-700 rounded text-xs text-zinc-300 transition-colors" data-testid="load-clip-btn">
                <Upload className="w-3.5 h-3.5" /> Load Video File
              </button>
              {sources.filter(s => s.type === SOURCE_TYPES.VIDEO).length > 0 && (
                <div className="mt-2 space-y-1">
                  {sources.filter(s => s.type === SOURCE_TYPES.VIDEO).map(s => (
                    <div key={s.id} className="flex items-center justify-between bg-zinc-900 px-2 py-1.5 rounded text-xs">
                      <div className="flex items-center gap-2">
                        <Film className="w-3 h-3 text-zinc-500" />
                        <span className="text-zinc-300">{s.label}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => switchToSource(s.id)} className={`px-2 py-0.5 rounded text-[10px] font-semibold ${s.id === programSourceId ? "bg-red-600 text-white" : "bg-zinc-700 text-zinc-400 hover:bg-zinc-600"}`}>
                          {s.id === programSourceId ? "ON AIR" : "TAKE"}
                        </button>
                        <button onClick={() => removeSource(s.id)} className="text-zinc-600 hover:text-red-400"><X className="w-3 h-3" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeBottomTab === "replay" && (
            <div>
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider font-bold mb-2">Capture Replay</p>
              <div className="flex items-center gap-2 flex-wrap">
                {/* Preset replay times */}
                {[5, 10, 15, 20, 30, 45, 60].map(t => (
                  <button
                    key={t}
                    onClick={() => captureReplay(t)}
                    disabled={!programSourceId || isPlayingReplay}
                    className="px-3 py-1.5 rounded text-xs font-semibold bg-yellow-700 hover:bg-yellow-600 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    data-testid={`replay-${t}s`}
                  >{t}s</button>
                ))}
                <div className="h-6 w-px bg-zinc-700" />
                {/* Custom replay time */}
                <div className="flex items-center gap-1">
                  <input
                    type="number" value={customReplayTime} onChange={e => setCustomReplayTime(e.target.value)}
                    placeholder="Custom" min="1" max="120"
                    className="bg-zinc-900 border border-zinc-700 text-white text-xs rounded px-2 py-1.5 w-20 focus:outline-none focus:border-yellow-500"
                    data-testid="custom-replay-input"
                  />
                  <button
                    onClick={() => { const t = parseInt(customReplayTime); if (t > 0 && t <= 120) captureReplay(t); else alert("Enter 1-120 seconds"); }}
                    disabled={!programSourceId || isPlayingReplay || !customReplayTime}
                    className="px-3 py-1.5 rounded text-xs font-semibold bg-yellow-700 hover:bg-yellow-600 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    data-testid="replay-custom-btn"
                  >Replay</button>
                </div>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <span className="text-[10px] text-zinc-500">Buffer: {replayDuration}s</span>
                <input type="range" min="15" max="120" value={replayDuration} onChange={e => setReplayDuration(Number(e.target.value))}
                  className="w-32 accent-yellow-500" />
                <span className="text-[10px] text-zinc-400">{replayDuration}s max buffer</span>
              </div>
            </div>
          )}
        </div>

        {/* Right: Overlay / ScoreBird Config */}
        <div className="w-80 border-l border-zinc-800/50 p-3 overflow-auto">
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider font-bold mb-2">ScoreBird Overlay</p>
          <div className="space-y-2">
            <input
              type="text" value={scorebirdLink} onChange={e => setScorebirdLink(e.target.value)}
              placeholder="ScoreBird NestCloud URL or nest_id"
              className="w-full bg-zinc-900 border border-zinc-700 text-white text-xs rounded px-2 py-1.5 focus:outline-none focus:border-orange-500"
              data-testid="scorebird-link-input"
            />
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setScorebirdActive(true); setShowScoreBug(true); }}
                disabled={!scorebirdLink}
                className="px-3 py-1 rounded text-[10px] font-bold bg-green-700 hover:bg-green-600 text-white disabled:opacity-40 transition-colors"
                data-testid="scorebird-activate-btn"
              >Show Score Bug</button>
              <button
                onClick={() => { setShowScoreBug(false); setScorebirdActive(false); }}
                className="px-3 py-1 rounded text-[10px] font-bold bg-zinc-700 hover:bg-zinc-600 text-zinc-300 transition-colors"
                data-testid="scorebird-hide-btn"
              >Hide</button>
            </div>
            {scorebirdData && (
              <div className="bg-zinc-900 rounded p-2 text-[10px] text-zinc-400 space-y-0.5">
                <div className="flex justify-between"><span>{scorebirdData.away_team_name || "Away"}</span><span className="font-bold text-white">{scorebirdData.away_score}</span></div>
                <div className="flex justify-between"><span>{scorebirdData.home_team_name || "Home"}</span><span className="font-bold text-white">{scorebirdData.home_score}</span></div>
                <div className="text-zinc-500">Q{scorebirdData.quarter} · {scorebirdData.clock}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ════ STATUS BAR ════ */}
      <div className="h-6 bg-[#16181b] border-t border-black/50 flex items-center px-3 text-[10px] text-zinc-500 gap-6 flex-shrink-0">
        <span>Sources: {sources.length}/4</span>
        <span>Output: {resolution}</span>
        <span>{streamStatus ? `Stream: ${streamStatus}` : "Stream: Idle"}</span>
        <div className="flex-1" />
        <span>{isLive ? "Status: Streaming" : "Status: Ready"}</span>
      </div>
    </div>
  );
}
